<template>
  <div class="config-container">
    <div class="config-grid">
      <div id="row1">
        <svg width="100%" height="100%" viewBox="0 0 1045 53.58">
          <text
            id="title"
            stroke="none"
            stroke-width="1"
            fill="#00413B"
            fill-rule="evenodd"
            font-family="SegoeUI, Segoe UI"
            font-size="40"
            font-weight="normal"
            x="0"
            y="1em"
          >
            Patient Information and Settings
          </text>
        </svg>
      </div>
      <div id="row2" class="grid-bg">
        <LabelledInput
          name="HDA Session Number"
          id="patient-id-input"
          initialValue="ZUHE4-VFLUK-P84F1"
          textAlign="left"
          :showDualUnit="false"
          :leftUnit="null"
        ></LabelledInput>
        <LabelledComboBox
          name="Surgery Type"
          id="surgery-type"
          initialValue="ZUHE4-VFLUK-P84F1"
          :options="surgeryTypes"
          @valueChanged="surgeryTypeChanged"
        ></LabelledComboBox>
      </div>
      <div id="row3" class="grid-bg">
        <div id="row3-grid">
          <LabelledInput
            name="Patient weight"
            id="weight-input"
            :initialValue="patientWeight"
            :showDualUnit="true"
            leftUnit="lbs"
            rightUnit="kg"
            :leftToRightConversion="0.453592"
            @valueChanged="weightChanged"
          ></LabelledInput>
          <LabelledInput
            name="Patient height"
            id="height-input"
            :showDualUnit="true"
            leftUnit="inches"
            rightUnit="cms"
            :leftToRightConversion="2.54"
            @valueChanged="heightChanged"
          ></LabelledInput>
          <LabelledInput
            id="age-input"
            name="Patient age"
            :showDualUnit="false"
            leftUnit="years"
            @valueChanged="ageChanged"
          ></LabelledInput>
          <div id="warning-cont">
            <svg
              id="warning-svg"
              viewBox="0 0 18 10"
              preserveAspectRatio="xMidYMid meet"
            >
              <text
                x="5.5"
                y="2.8"
                text-anchor="start"
                font-size="2"
                fill="#373737"
              >
                Warning
              </text>
              <text text-anchor="start" font-size="1.3" fill="#373737">
                <tspan x="1" y="6">This product is certified for</tspan>
                <tspan x="1" dy="1.2em">use on patients aged over</tspan>
                <tspan x="1" dy="1.2em">18 only.</tspan>
              </text>
              <image
                x="1"
                y="0.5"
                width="3.3"
                height="3.3"
                id="alert-img"
                href="../assets/alert.svg"
              />
            </svg>
          </div>
        </div>
      </div>
      <div id="row4" class="grid-bg">
        <div id="row4-grid">
          <div class="label">Target Mean Arterial Pressure MAP (mmHg)</div>
          <TargetRangeSlider
            id="upper-target-slider"
            :min="40"
            :max="120"
            :gap="10"
            :allowHypo="allowHypo"
            :hypoThreshold="65"
            :lowerValue="lowerRange"
            :upperValue="upperRange"
            @range-changed="rangeChanged"
          ></TargetRangeSlider>
          <div id="hypo-check-cont">
            <SquareRadioButton
              :value="allowHypo"
              @check="checkChanged"
            ></SquareRadioButton>
          </div>
          <div id="ranges-cont">
            <div id="ranges-title-cont">
              <svg
                id="ranges-title-svg"
                viewBox="0 0 60 10"
                preserveAspectRatio="xMidYMid"
              >
                <text
                  x="30.5"
                  y="7"
                  text-anchor="middle"
                  font-size="5"
                  fill="#373737"
                >
                  Cumulative BP Thresholds
                </text>
              </svg>
            </div>
            <div id="target-range-cont">
              <svg
                class="range-svg"
                viewBox="0 0 66.4 10"
                preserveAspectRatio="xMidYMid"
              >
                <text x="33.2" y="7" text-anchor="middle" font-size="7">
                  {{ lowerRange }} - {{ upperRange }} mmHg
                </text>
              </svg>
            </div>
            <div id="mild-range-cont">
              <svg
                class="range-svg"
                viewBox="0 0 66.4 10"
                preserveAspectRatio="xMidYMid"
              >
                <text x="33.2" y="7" text-anchor="middle" font-size="7">
                  55 - {{ mildHypoThreshold }} mmHg
                </text>
              </svg>
            </div>
            <div id="severe-range-cont">
              <svg
                class="range-svg"
                viewBox="0 0 66.4 10"
                preserveAspectRatio="xMidYMid"
              >
                <text
                  x="33.2"
                  y="7"
                  text-anchor="middle"
                  font-size="7"
                  fill="white"
                >
                  &lt; 55 mmHg
                </text>
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div id="row5" class="grid-bg">
        <div id="row5-grid">
          <div id="brightness-label">Display Brightness</div>
          <SimpleSlider
            id="brightness-slider"
            v-model="brightness"
            min="50"
            max="100"
          ></SimpleSlider>
        </div>
      </div>
      <div id="logo"></div>
    </div>
  </div>
</template>

<script>
import LabelledInput from '../components/LabelledInput.vue'
import LabelledComboBox from '../components/LabelledComboBox.vue'
import SimpleSlider from '../components/SimpleSlider.vue'
import TargetRangeSlider from '../components/TargetRangeSlider.vue'
import SquareRadioButton from '../components/SquareRadioButton'
import { useAppStore } from '@/stores/app'

export default {
  name: 'RunningModeMain',
  setup() {
    const store = useAppStore()
    return { store }
  },
  props: {
    patientWeight: Number
  },
  data() {
    return {
      allowHypo: false,
      surgeryTypes: [
        { id: 0, desc: 'Cardiac' },
        { id: 1, desc: 'Neuro' },
        { id: 2, desc: 'General' },
        { id: 3, desc: 'Urology' },
        { id: 4, desc: 'Gynecology' },
        { id: 5, desc: 'ENT' },
        { id: 6, desc: 'Plastics' },
        { id: 7, desc: 'Vascular' },
        { id: 8, desc: 'Thoracic' },
        { id: 9, desc: 'Orthopedics' },
        { id: 10, desc: 'Other' }
      ]
    }
  },
  components: {
    LabelledInput,
    LabelledComboBox,
    SimpleSlider,
    TargetRangeSlider,
    SquareRadioButton
  },
  methods: {
    weightChanged(value) {
      this.$emit('patientWeightChanged', value)
    },
    heightChanged(value) {
      this.$emit('patientHeightChanged', value)
    },
    ageChanged(value) {
      this.$emit('patientAgeChanged', value)
    },
    rangeChanged(lower, upper) {
      this.store.setMildHypoThreshold(Math.max(65, lower))
      this.store.setBPTargetMin(lower)
      this.store.setBPTargetMax(upper)
    },
    checkChanged(allowHypo) {
      this.allowHypo = allowHypo
      if (!this.allowHypo && this.lowerRange < 65) {
        this.store.setBPTargetMin(65)
        if (this.upperRange < this.lowerRange + 10)
          this.store.setBPTargetMax(85)
      }
    },
    surgeryTypeChanged(value) {
      this.$emit('surgeryTypeChanged', value)
    }
  },
  computed: {
    upperRange: {
      get() {
        return this.store.bpTargetMax
      }
    },
    lowerRange: {
      get() {
        return this.store.bpTargetMin
      }
    },
    mildHypoThreshold: {
      get() {
        return this.store.mildHypoThreshold
      }
    },
    brightness: {
      get() {
        return this.store.brightness * 100
      },
      set(value) {
        this.store.setBrightness(value / 100)
      }
    }
  }
}
</script>

<style scoped>
text {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: black;
}
.label {
  color: black;
}
.config-container {
  display: block;
  box-sizing: border-box;
  background-color: #add4c8;
  margin: 0;
  padding: 1%;
  height: 100%;
  width: 100%;
}
.config-grid {
  display: grid;
  box-sizing: border-box;
  background-color: white;
  height: 100%;
  width: 100%;
  grid-template-rows: 9% 14% 27% 32% 14%;
  grid-template-columns: 80% 20%;
  row-gap: 1%;
  padding: 0 1% 1% 1%;
}
.grid-bg {
  background: #efefef;
  border: 1px solid #808080;
}
#row1 {
  grid-row: 1;
  grid-column-start: 1;
  grid-column-end: 3;
}
#title {
  text-align: left;
}
#row2 {
  grid-row: 2;
  grid-column-start: 1;
  grid-column-end: 3;
  box-sizing: border-box;
  padding-bottom: 1%;
  padding-left: 1%;
  padding-right: 1%;
  display: grid;
  grid-template-columns: 49.5% 49.5%;
  grid-column-gap: 1%;
}
#patient-id-input {
  height: 100%;
  width: 100%;
  font-size: 1.7vw;
}
#surgery-type {
  height: 100%;
  width: 100%;
  font-size: 1.7vw;
  grid-column: 2;
}
#row3 {
  grid-row: 3;
  grid-column-start: 1;
  grid-column-end: 3;
  box-sizing: border-box;
  padding: 1%;
  font-size: 1.7vw;
}
#row3-grid {
  height: 100%;
  display: grid;
  grid-template-rows: 49.5% 49.5%;
  row-gap: 1%;
  grid-template-columns: 49.33% 24.33% 24.33%;
  column-gap: 1%;
}
#weight-input {
  grid-row: 1;
  grid-column: 1;
}
#height-input {
  grid-row: 2;
  grid-column: 1;
}
#age-input {
  grid-row: 1;
  grid-column: 2;
  max-width: 100%;
}
#warning-cont {
  box-sizing: border-box;
  fill: #818181;
  padding: 1%;
  grid-row-start: 1;
  grid-row-end: 3;
  grid-column: 3;
  border: 1px solid #a9a9a9;
}
#warning-svg {
  height: 100%;
  width: 100%;
}
#row4 {
  grid-row: 4;
  grid-column-start: 1;
  grid-column-end: 3;
  box-sizing: border-box;
  padding: 1%;
  text-align: left;
  font-size: 1.7vw;
}
#row4-grid {
  height: 100%;
  display: grid;
  grid-template-columns: 75.67% 24.33%;
  grid-template-rows: 20% 40% 40%;
}
#upper-target-slider {
  grid-row: 2;
  grid-column: 1;
  padding-right: 1%;
}
#hypo-check-cont {
  grid-row: 3;
  grid-column: 1;
  margin-top: 3%;
  margin-left: 2%;
}
#ranges-cont {
  grid-row-start: 1;
  grid-row-end: 4;
  grid-column: 2;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  padding: 1%;
  border: 1px solid #a9a9a9;
  display: grid;
  grid-template-rows: 25% 25% 25% 25%;
  grid-template-columns: 100%;
}
#ranges-title-cont {
  height: 100%;
  width: 100%;
}
#ranges-title-svg {
  height: 100%;
  width: 100%;
}
#target-range-cont {
  grid-row: 2;
  background-color: #c4eecb;
  margin: 1.5% 7% 1.5% 7%;
  width: 86%;
}
#mild-range-cont {
  grid-row: 3;
  background-color: #c4c4c4;
  margin: 1.5% 7% 1.5% 7%;
  width: 86%;
}
#severe-range-cont {
  grid-row: 4;
  background-color: #707070;
  margin: 1.5% 7% 2% 7%;
  width: 86%;
}
.range-svg {
  height: 100%;
  width: 100%;
}
#row5 {
  grid-row: 5;
  grid-column: 1;
  box-sizing: border-box;
  padding: 1%;
  text-align: left;
  font-size: 1.7vw;
}
#row5-grid {
  height: 100%;
  display: grid;
  grid-template-rows: 40% 60%;
  grid-template-columns: 15% 80% 5%;
  align-items: center;
}
#brightness-label {
  grid-column-start: 1;
  grid-column-end: 3;
  color: black;
}
#brightness-slider {
  grid-row: 2;
  grid-column: 2;
}
#logo {
  grid-row: 5;
  grid-column: 2;
  background: url('../assets/dslogo.svg') no-repeat center;
  background-size: 95%;
}
</style>
